import React, {useState, Fragment, useRef, useEffect} from 'react';
import './ChatActions.scss';
import {useForm} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {IconButtonMUI} from '../../../../../shared';
import {ReactComponent as SendIcon} from '../../../../../assets/icons/send.svg';
import {ReactComponent as AddIcon} from '../../../../../assets/icons/add.svg';
import {ReactComponent as RemoveIcon} from '../../../../../assets/icons/remove.svg';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {
    postProjectChatsMessage
} from '../../../../../redux/actions/projects/projectsActions';
import {notifyError} from '../../../../../helpers/notifySnack';
import {
    concatFileExtension,
    convertSizeUi,
    convertSizeToMB,
    getIconForFormat, hasPermission,
} from '../../../../../helpers/functions';
import {useDropzone} from 'react-dropzone';
import {ReactComponent as CloseIcon} from '../../../../../assets/icons/closeLarge.svg';
import AutoHeightTextarea from "../../../AutoHeightTextArea/AutoHeightTextArea";
import {removeFirstMatchMessageFromArr, setAddMessagesSentArr} from "../../../../../redux/actions/chat/chatActions";
import {PERMISSIONS} from "../../../../../helpers/constants";

const ChatActions = ({singleProjectId, chatId, messagesEndRef, respondMessage, handleCloseMenu, fetchMessages}) => {
    const {t} = useTranslation();
    const chatActionsRef = useRef(null);
    const [message, setMessage] = useState('');
    const [acceptedFiles, setAcceptedFiles] = useState([]);
    const [buttonLoad, setButtonLoad] = useState(false);
    const maxSizeMb = 50;
    const maxFiles = 50;
    const dispatch = useDispatch();
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const singleProject = useSelector((state) => state.projects.singleProject);
    const profileMe = useSelector((state) => state.profile.profileMe);

    const {chatMessagesSend} = useSelector((state => state.chat))
    const permissionSending = hasPermission(PERMISSIONS.CHAT_MESSAGES, profileMe?.permissions ?? []);
    const allowSending = (singleProject?.status === 'active' || singleProject?.status === 'pause') && permissionSending
    const validationSchema = yup.object().shape({});

    const {setValue} = useForm({
        mode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            items: []
        }
    });

    const onFilesAdded = (files) => {
        setValue('items', files, {shouldValidate: true});
    };

    const maxSizeValidator = (file) => {
        if (!file || file?.kind === 'file') return
        if (file.name.endsWith('.exe') || file.name.endsWith('.bin')) {
            notifyError(t('Dropzone.invalidFileType'));
            return {
                code: 'invalid format'
            };
        }

        if (file && convertSizeToMB(file.size) > maxSizeMb) {
            notifyError(`${t('Dropzone.fileSizeError')} ${maxSizeMb}MB`);
            return {
                code: 'large size'
            };
        }
        return null;
    };

    const onDrop = (newFiles) => {
        const filesWithId = newFiles.map((file) =>
            Object.assign(file, {
                id: `${file.name}-${file.lastModified}-${Math.random()}`
            })
        );
        const files = [...acceptedFiles, ...filesWithId];

        setAcceptedFiles(files);
        onFilesAdded(files);
    };


    const removeFile = (fileToRemove) => {
        const files = acceptedFiles.filter((file) => file.id !== fileToRemove.id);
        setAcceptedFiles(files);
        onFilesAdded(files);
    };


    const {getRootProps, getInputProps} = useDropzone({
        onDrop,
        maxFiles: maxFiles,
        validator: maxSizeValidator,
    });

    const files = acceptedFiles.map((file, idx) => (
        <div className={'chat-dropzone-item'} key={file.path + '' + idx}>
            <div className='chat-dropzone-details'>
                <div className='chat-dropzone-img'>
                    <img src={getIconForFormat(file.name.split('.').pop())} alt={file.name}/>
                </div>
                <div className='chat-dropzone-info'>
                    <p className={'chat-dropzone-name'}>{concatFileExtension(file.name)}</p>
                    <p className={'chat-dropzone-size'}>{convertSizeUi(file.size)}</p>
                </div>
            </div>
            <div className='chat-dropzone-remove' onClick={() => removeFile(file)}>
                <RemoveIcon/>
            </div>
        </div>
    ));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim() && !acceptedFiles.length) return;
        setButtonLoad(true);

        try {
            const formData = new FormData();

            if (message.trim()) {
                formData.append('message', message);
            }

            if (Object.keys(respondMessage).length) {
                formData.append('parentId', respondMessage.id);
            }
            let videoIdx = 0;

            if (acceptedFiles.length) {
                acceptedFiles.forEach((file, idx) => {
                    if (file.name.toLowerCase().endsWith('.mp4') || file.name.toLowerCase().endsWith('.avi') || file.name.toLowerCase().endsWith('.mov')) {
                        formData.append(`videos[${videoIdx}]`, file);
                        videoIdx++;
                    } else {
                        formData.append(`files[${idx}]`, file);
                    }
                });
            }

            const res = await dispatch(postProjectChatsMessage(singleProjectId, chatId, formData));
            if (res.payload?.status === 200) {
                // await fetchMessages();
                dispatch(setAddMessagesSentArr('message'))
                // setMessage('');
                // setAcceptedFiles([]);
                handleCloseMenu();
            }
        } finally {
            setButtonLoad(false);
        }
    };

    const handleOnPaste = (event) => {
        const items = event.clipboardData.items;
        const pastedFiles = [];

        for (let item of items) {
            if (item.kind === "file") {
                const file = item.getAsFile();
                if (file) {
                    pastedFiles.push(Object.assign(file, {id: `${file.name}-${file.lastModified}-${Math.random()}`}));
                }
            }
        }

        if (pastedFiles.length > 0) {
            setAcceptedFiles((prevFiles) => [...prevFiles, ...pastedFiles]);
            setValue('items', [...acceptedFiles, ...pastedFiles], {shouldValidate: true});
        }
    };

    useEffect(() => {
        if(!chatMessagesSend.length){
            setMessage('')
            setAcceptedFiles([])
        }
    }, [chatMessagesSend]);

    return (
        <Fragment>
            {allowSending &&  <form {...getRootProps({className: `chat-actions}`, ref: chatActionsRef,})} onSubmit={onSubmit}>
                <input {...getInputProps()}  />
                {Object.keys(respondMessage).length ? (
                    <div className='chat-respond' onClick={(e) => e.stopPropagation()}>
                        <div className='chat-respond-data'>
                            <div className='chat-respond-info'>
                                <div
                                    className='chat-respond__author'>{t('all.answer')} {respondMessage.sender.firstName}</div>
                                <div className='chat-respond__msg'>
                                    {respondMessage.message || respondMessage.files?.map((file) => file.name).join(', ') || t('all.filee')}
                                </div>
                            </div>
                        </div>
                        <div className='chat-respond__close' onClick={handleCloseMenu}>
                            <CloseIcon/>
                        </div>
                    </div>
                ) : null}
                <div className="chat-actions-group" onPaste={handleOnPaste}>
                    <div className='chat-actions__text' onClick={(e) => e.stopPropagation()}>
                        {files.length > 0 && (
                            <div className={'chat-dropzone-files'}>
                                <div className={'chat-dropzone-list'}>{files}</div>
                            </div>
                        )}
                        <AutoHeightTextarea
                            projectChatRef={messagesEndRef}
                            chatActionsRef={chatActionsRef}
                            className='chat-actions__textarea'
                            value={message}
                            maxLength={1999}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={t('ChatActions.message')}
                            onKeyDown={(e) => {
                                if (buttonLoad || chatMessagesSend.length) {
                                    e.preventDefault();
                                }
                                if ((e.key === 'Enter' && !e.shiftKey ) && !isMobile) {
                                    e.preventDefault();
                                    if (!buttonLoad && e.target?.value !== '' && !chatMessagesSend.length) {
                                        onSubmit(e);
                                    }
                                }
                            }}
                            files={files}
                            respondMessage={respondMessage}
                        />
                    </div>
                    <div className='chat-actions__button'>
                        <div>
                            <div className={`chat-dropzone-content`}>
                                <IconButtonMUI title={t('all.file')}>
                                    <AddIcon/>
                                </IconButtonMUI>
                            </div>
                        </div>

                        <IconButtonMUI
                            title={t('all.send')}
                            type='submit'
                            loading={buttonLoad || Boolean(chatMessagesSend.length)}
                            onClick={(e) => e.stopPropagation()}
                            // disabled={!message.trim() && !acceptedFiles.length}
                        >
                            <SendIcon/>
                        </IconButtonMUI>
                    </div>
                </div>

            </form>}
        </Fragment>
    );
};

export default ChatActions;
